import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { CASES } from "@/constants/testIds";
import { useT } from "@/lib/i18n";

const IMAGES = [
  "https://customer-assets.emergentagent.com/job_bodega-craft/artifacts/qkg7rkgz_15238085654604241855.png",
  "https://customer-assets.emergentagent.com/job_bodega-craft/artifacts/hkl0kz5g_1896205914577791182.png",
  "https://customer-assets.emergentagent.com/job_bodega-craft/artifacts/432ntz3i_5364081606375367356.png",
  "https://customer-assets.emergentagent.com/job_bodega-craft/artifacts/szm45yyj_9949500231808024683.png",
  "https://customer-assets.emergentagent.com/job_bodega-craft/artifacts/mycno50l_msport01_Page_12.png",
  "https://customer-assets.emergentagent.com/job_bodega-craft/artifacts/lbniwipf_Credenciales_LIMON_Page_06.png",
];

export const CaseStudies = () => {
  const { t } = useT();
  const c = t.cases;
  return (
    <section
      id="work"
      data-testid={CASES.section}
      className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32 border-b border-white/10"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-20">
        <div className="md:col-span-7">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#ff3b00] mb-4">
            {c.eyebrow}
          </div>
          <h2 className="font-display uppercase text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight">
            {c.titleA}<br />
            <span className="text-white/40">{c.titleB}</span>
          </h2>
        </div>
        <div className="md:col-span-4 md:col-start-9 flex md:items-end">
          <div className="font-mono text-xs tracking-[0.25em] uppercase text-white/40">
            <div className="text-white text-2xl md:text-3xl font-display tracking-tight mb-1">
              {c.stat}<span className="text-[#ff3b00]">{c.statSuffix}</span>
            </div>
            {c.lede}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">
        {c.items.map((item, i) => (
          <motion.article
            key={item.code}
            data-testid={CASES.card(i)}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.07 }}
            className="group relative bg-[#0a0a0a] border border-white/10 hover:border-[#ff3b00]/60 transition-colors duration-300"
          >
            <div className="relative aspect-[5/4] overflow-hidden bg-[#141414]">
              <img
                src={IMAGES[i]}
                alt={item.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700 ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/30 to-transparent" />
              <div className="absolute top-4 left-4 right-4 flex items-start justify-between font-mono text-[10px] tracking-[0.3em] uppercase">
                <span className="text-white/80 bg-black/40 backdrop-blur px-2 py-1 border border-white/20">
                  {item.code}
                </span>
                <span className="text-black bg-[#ff3b00] px-2 py-1">
                  {item.tag}
                </span>
              </div>
              <div className="absolute bottom-4 left-4 right-4 font-mono text-[10px] tracking-[0.3em] uppercase text-white/60">
                {item.sector}
              </div>
            </div>

            <div className="p-6 md:p-8">
              <h3 className="font-display uppercase text-2xl md:text-3xl leading-[0.95] tracking-tight text-white mb-5">
                {item.title}
              </h3>

              <dl className="grid grid-cols-1 gap-3 font-mono text-xs">
                <div className="grid grid-cols-[100px_1fr] gap-3 py-2 border-t border-white/10">
                  <dt className="tracking-[0.25em] uppercase text-white/40">{c.labels.client}</dt>
                  <dd className="text-white">{item.client}</dd>
                </div>
                <div className="grid grid-cols-[100px_1fr] gap-3 py-2 border-t border-white/10">
                  <dt className="tracking-[0.25em] uppercase text-white/40">{c.labels.roster}</dt>
                  <dd className="text-white/70">{item.detail}</dd>
                </div>
                <div className="grid grid-cols-[100px_1fr] gap-3 py-2 border-t border-b border-white/10">
                  <dt className="tracking-[0.25em] uppercase text-white/40">{c.labels.methods}</dt>
                  <dd className="flex flex-wrap gap-1.5">
                    {item.methods.map((m) => (
                      <span
                        key={m}
                        className="text-[10px] tracking-[0.2em] uppercase text-white/80 px-2 py-1 border border-white/15 group-hover:border-[#ff3b00]/50 transition-colors"
                      >
                        {m}
                      </span>
                    ))}
                  </dd>
                </div>
              </dl>

              <div className="mt-6 flex items-center justify-between font-mono text-xs tracking-[0.25em] uppercase text-white/40 group-hover:text-[#ff3b00] transition-colors">
                <span>{c.labels.view}</span>
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" strokeWidth={2} />
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
};
